import React, { useState } from 'react';
import styled from 'styled-components';
import Link from '@components/Link';
import { useLazyQuery } from '@apollo/client';
import { Trans } from '@lingui/react';
import gql from 'graphql-tag';
import { Box } from '@components/layout/Grid';
import Span from '@components/layout/Span';
import ThumbnailWithRatio from '@components/ThumbnailWithRatio';
import { P } from '@components/typography/Paragraph';
import InstructorModalSkeleton from '@skeletons/InstructorModalSkeleton';
import { Body, Footer, Header, Modal } from './base';
import emptyResults from '@components/EmptyResults';
import BrokenFileSVG from '@assets/BrokenFileIcon';

const GET_INSTRUCTOR = gql`
  query instructor($id: ID!) {
    instructor(id: $id) {
      id
      slug
      name
      slug
      description
      assets {
        externalImage
      }
      speciality
      location
      webpage
    }
  }
`;

export const ButtonSC = styled.a`
  display: block;
  width: 100%;
  text-align: center;
  margin: 0;
  color: white;
  margin-left: 5px;
  margin-right: 5px;
  padding: 12px 25px;
  border-radius: 2px;
  border: 1px solid ${props => props.theme.colors.primary['blue-2']};
  background-color: ${props => props.theme.colors.primary['blue-2']};
  font-weight: 600;
  color: #fff;
  text-decoration: none;
  font-size: 13px;
  cursor: pointer;
`;

const EmptyResults = styled(emptyResults)`
  text-align: center;
`;

const BrokenFile = styled(BrokenFileSVG)`
  width: 100px;
  height: 100px;
  fill: ${({ theme }) => theme.colors.neutrals['gray-3']};
`;

const InstructorModal = props => {
  const [open, setOpen] = useState(false);
  const [loadInstructor, { loading, data }] = useLazyQuery(GET_INSTRUCTOR, {
    variables: { id: props.slug },
  });

  const handleOpenModal = async () => {
    await loadInstructor();
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  let content = null;
  if (loading) {
    content = (
      <Body>
        <InstructorModalSkeleton />
      </Body>
    );
  } else if (data?.instructor) {
    const { instructor } = data;
    content = (
      <>
        <Body pushFooterDown={120}>
          <Box mb={30}>
            <ThumbnailWithRatio path={instructor.assets.externalImage} />
          </Box>
          <Box mb={30}>
            <P color="#242424">
              <Span bold color="#242424">
                <Trans id="teacher_modal.speciality" message={'Speciality'} />:
              </Span>{' '}
              {instructor.speciality}
            </P>

            <P color="#242424">
              <Span bold color="#242424">
                <Trans id="teacher_modal.location" message={'Location'} />:
              </Span>{' '}
              {instructor.location}
            </P>
            <P color="#242424">
              <Span bold color="#242424">
                <Trans id="teacher_modal.homepage" message={'Homepage'} />:
              </Span>{' '}
              <Link href={instructor.webpage} target={'_blank'}>
                {instructor.webpage}
              </Link>
            </P>
          </Box>
          <Box mb={30}>
            <P
              lineHeight="23px"
              color="#242424"
              dangerouslySetInnerHTML={{
                __html: instructor.description,
              }}
            />
          </Box>
        </Body>
        <Footer>
          <ButtonSC href={`/team/${instructor.slug}`} target="_blank">
            <Trans id="teacher_modal.go_to_profile" message={'Visit profile'} />
          </ButtonSC>
        </Footer>
      </>
    );
  } else {
    content = (
      <Body>
        <EmptyResults
          title=""
          description={
            <Trans
              id="public_profile.error_msg"
              message={`Sorry, it seems like there's an issue displaying the profile. Please try again in a bit`}
            />
          }
        >
          <BrokenFile />
        </EmptyResults>
      </Body>
    );
  }

  return (
    <>
      {props.children(handleOpenModal)}
      <Modal
        open={open}
        handleClose={handleCloseModal}
        customWidth="600px"
        data-testid={props['data-testid']}
      >
        <Header handleClose={handleCloseModal} />
        {content}
      </Modal>
    </>
  );
};

export default InstructorModal;
